import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Form, Modal, Spinner, Table, Toast, ToastContainer } from 'react-bootstrap';
import { FiAlertTriangle, FiCheckCircle, FiArrowRight, FiX } from 'react-icons/fi';
import { changePolicyStatus } from '../../../services/api';
import './ManualReviewPolicies.css';

export const TRIP_POLICY_STATUS_OPTIONS = [
    'Created',
    'CG confirmed',
    'Paid',
    'Cancelled',
    'RIB Received',
];

const STATUS_VARIANTS = {
    created: 'mrp-status--neutral',
    'cg confirmed': 'mrp-status--info',
    paid: 'mrp-status--success',
    cancelled: 'mrp-status--danger',
    'rib received': 'mrp-status--accent',
};

const StatusBadge = ({ status }) => {
    const key = String(status || '').toLowerCase();
    return (
        <Badge bg="" className={`mrp-status ${STATUS_VARIANTS[key] || 'mrp-status--neutral'}`}>
            {status || '—'}
        </Badge>
    );
};

const ManualReviewPolicies = ({ policies, onDismiss }) => {
    const [rows, setRows] = useState({});
    const [confirmTarget, setConfirmTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [toast, setToast] = useState(null);
    const cancelButtonRef = useRef(null);

    const getRow = (policy) => rows[policy.policy_id] || {
        currentStatus: policy.current_status || '',
        selected: '',
        updated: false,
    };

    const patchRow = (policy, patch) => {
        setRows((prev) => ({
            ...prev,
            [policy.policy_id]: { ...getRow(policy), ...prev[policy.policy_id], ...patch },
        }));
    };

    const openConfirm = (policy) => {
        const row = getRow(policy);
        setModalError(null);
        setConfirmTarget({
            policy,
            currentStatus: row.currentStatus,
            newStatus: row.selected,
        });
    };

    const closeConfirm = () => {
        if (saving) return;
        setConfirmTarget(null);
        setModalError(null);
    };

    const handleConfirm = async () => {
        if (!confirmTarget || saving) return;
        const { policy, newStatus } = confirmTarget;
        setSaving(true);
        setModalError(null);
        try {
            await changePolicyStatus(policy.policy_id, newStatus);
            patchRow(policy, { currentStatus: newStatus, selected: '', updated: true });
            setConfirmTarget(null);
            setToast({ variant: 'success', message: 'Le statut du contrat a été mis à jour avec succès.' });
        } catch (error) {
            setModalError('Impossible de modifier le statut. Veuillez réessayer.');
        } finally {
            setSaving(false);
        }
    };

    const pendingCount = policies.filter((p) => !getRow(p).updated).length;

    return (
        <section className="mrp" aria-labelledby="mrp-title">
            <Alert variant="warning" className="mrp-alert" dismissible={Boolean(onDismiss)} onClose={onDismiss}>
                <div className="mrp-alert__content">
                    <FiAlertTriangle className="mrp-alert__icon" aria-hidden="true" />
                    <div>
                        <h3 id="mrp-title" className="mrp-alert__title">Vérification manuelle requise</h3>
                        <p className="mrp-alert__text">
                            Le client a été mis à jour avec succès. Cependant, une vérification manuelle est
                            requise pour les contrats suivants :
                        </p>
                        <p className="mrp-alert__meta">
                            {pendingCount > 0
                                ? `${pendingCount} contrat${pendingCount > 1 ? 's' : ''} à vérifier`
                                : 'Tous les contrats ont été traités'}
                        </p>
                    </div>
                </div>
            </Alert>

            <div className="mrp-table-wrap">
                <Table className="mrp-table mb-0" responsive>
                    <thead>
                        <tr>
                            <th scope="col">Contrat</th>
                            <th scope="col">Statut</th>
                            <th scope="col">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {policies.map((policy) => {
                            const row = getRow(policy);
                            const selectId = `mrp-status-${policy.policy_id || policy.policy_code}`;
                            const canSubmit = Boolean(row.selected) && row.selected !== row.currentStatus;
                            return (
                                <tr key={policy.policy_id || policy.policy_code}>
                                    <td data-label="Contrat">
                                        {policy.policy_id ? (
                                            <Link className="mrp-link" to={`/admin/trips/policies/${policy.policy_id}`}>
                                                {policy.policy_code || policy.policy_id}
                                            </Link>
                                        ) : (
                                            <span>{policy.policy_code || '—'}</span>
                                        )}
                                    </td>
                                    <td data-label="Statut">
                                        <StatusBadge status={row.currentStatus} />
                                    </td>
                                    <td data-label="Action">
                                        <div className="mrp-action">
                                            {row.updated ? (
                                                <span className="mrp-action__label mrp-action__label--success">
                                                    <FiCheckCircle aria-hidden="true" /> Statut mis à jour
                                                </span>
                                            ) : (
                                                <span className="mrp-action__label mrp-action__label--warning">
                                                    <FiAlertTriangle aria-hidden="true" /> Vérification manuelle requise
                                                </span>
                                            )}
                                            {policy.policy_id && (
                                                <div className="mrp-action__controls">
                                                    <label htmlFor={selectId} className="visually-hidden">
                                                        Nouveau statut du contrat {policy.policy_code}
                                                    </label>
                                                    <Form.Select
                                                        id={selectId}
                                                        className="mrp-select"
                                                        value={row.selected}
                                                        onChange={(e) => patchRow(policy, { selected: e.target.value })}
                                                    >
                                                        <option value="">Nouveau statut…</option>
                                                        {TRIP_POLICY_STATUS_OPTIONS.map((status) => (
                                                            <option
                                                                key={status}
                                                                value={status}
                                                                disabled={status === row.currentStatus}
                                                            >
                                                                {status === row.currentStatus ? `${status} (actuel)` : status}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                    <Button
                                                        variant="primary"
                                                        className="mrp-button"
                                                        onClick={() => openConfirm(policy)}
                                                        disabled={!canSubmit}
                                                        title={canSubmit ? undefined : 'Sélectionnez un nouveau statut'}
                                                    >
                                                        Changer le statut
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            <Modal
                show={Boolean(confirmTarget)}
                onHide={closeConfirm}
                onEntered={() => cancelButtonRef.current?.focus()}
                backdrop={saving ? 'static' : true}
                keyboard={!saving}
                centered
                aria-labelledby="mrp-confirm-title"
                className="mrp-modal"
            >
                <Modal.Header closeButton={!saving}>
                    <Modal.Title id="mrp-confirm-title">Confirmer le changement de statut</Modal.Title>
                </Modal.Header>
                {confirmTarget && (
                    <Modal.Body>
                        <p className="mb-3">
                            Vous êtes sur le point de modifier le statut du contrat{' '}
                            <strong>{confirmTarget.policy.policy_code || confirmTarget.policy.policy_id}</strong>.
                        </p>
                        <div className="mrp-modal__transition">
                            <div className="mrp-modal__status">
                                <span className="mrp-modal__status-label">Statut actuel</span>
                                <StatusBadge status={confirmTarget.currentStatus} />
                            </div>
                            <FiArrowRight className="mrp-modal__arrow" aria-hidden="true" />
                            <div className="mrp-modal__status">
                                <span className="mrp-modal__status-label">Nouveau statut</span>
                                <StatusBadge status={confirmTarget.newStatus} />
                            </div>
                        </div>
                        <p className="mrp-modal__notice">
                            <FiAlertTriangle aria-hidden="true" />
                            Cette modification sera enregistrée et pourra avoir un impact sur le traitement du contrat.
                        </p>
                        {modalError && (
                            <Alert variant="danger" className="mb-0 mt-3" role="alert">
                                {modalError}
                            </Alert>
                        )}
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <Button
                        ref={cancelButtonRef}
                        variant="outline-secondary"
                        className="mrp-button"
                        onClick={closeConfirm}
                        disabled={saving}
                    >
                        Annuler
                    </Button>
                    <Button variant="primary" className="mrp-button" onClick={handleConfirm} disabled={saving}>
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" aria-hidden="true" />
                                Enregistrement…
                            </>
                        ) : (
                            'Confirmer le changement'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer position="top-end" className="mrp-toast-container">
                <Toast
                    show={Boolean(toast)}
                    onClose={() => setToast(null)}
                    delay={4000}
                    autohide
                    className={`mrp-toast mrp-toast--${toast?.variant || 'success'}`}
                    role="status"
                    aria-live="polite"
                >
                    <Toast.Body className="mrp-toast__body">
                        <FiCheckCircle aria-hidden="true" />
                        <span>{toast?.message}</span>
                        <button
                            type="button"
                            className="mrp-toast__close"
                            onClick={() => setToast(null)}
                            aria-label="Fermer la notification"
                        >
                            <FiX aria-hidden="true" />
                        </button>
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </section>
    );
};

export default ManualReviewPolicies;
